import { useState, ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Mail, Zap, Clock, Shield } from 'lucide-react';
import { ForecasterLogo } from '../components/ForecasterLogo';

/**
 * Trial Request Page
 *
 * Dedicated page for trial signup form
 */
export const Trial = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        workEmail: '',
        company: '',
        teamSize: '',
        useCase: '',
        notes: '',
    });
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setStatus('submitting');
        setErrorMessage('');
        try {
            const response = await fetch('/api/trial-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            if (!response.ok) {
                throw new Error('Submission failed');
            }
            setStatus('success');
            setFormData({
                fullName: '',
                workEmail: '',
                company: '',
                teamSize: '',
                useCase: '',
                notes: '',
            });
        } catch (error) {
            console.error(error);
            setErrorMessage('Something went wrong. Email forecaster.k8s@gmail.com and we will get you set up.');
            setStatus('error');
        }
    };

    const trialSteps = [
        {
            icon: Mail,
            title: 'Request access',
            description: 'Get Helm credentials within 1 business day.',
            time: '30 sec',
        },
        {
            icon: Zap,
            title: 'Install',
            description: 'One Helm command. Isolated namespace.',
            time: '5 min',
        },
        {
            icon: Clock,
            title: 'Monitor',
            description: 'Add annotation. Watch GPU metrics.',
            time: 'Instant',
        },
        {
            icon: Shield,
            title: 'Evaluate',
            description: 'See actual GPU utilization.',
            time: '14 days',
        },
    ];

    return (
        <div className="min-h-screen bg-obsidian-900 text-white antialiased">
            {/* Navbar */}
            <nav className="fixed top-0 inset-x-0 z-50 bg-obsidian-900/80 backdrop-blur-xl border-b border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/">
                        <ForecasterLogo />
                    </Link>
                    <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                        ← Back
                    </Link>
                </div>
            </nav>

            {/* Main Content */}
            <section className="pt-32 pb-24 bg-obsidian-900 relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-white/5 rounded-full blur-[150px]" />

                <div className="relative z-10 max-w-6xl mx-auto px-6">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                            Start your <span className="bg-gradient-to-r from-white via-gray-100 to-gray-200 bg-clip-text text-transparent">14-day trial</span>
                        </h1>
                        <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-6">
                            <span className="text-white font-semibold">Your environment. Your workloads.</span> No fake demos.
                        </p>
                        <p className="text-lg text-white font-semibold">
                            Zero code • Zero credit card • Zero lock-in
                        </p>
                    </div>

                    {/* Trial steps */}
                    <div className="grid md:grid-cols-4 gap-6 mb-16">
                        {trialSteps.map((item, i) => (
                            <div key={i} className="relative">
                                {/* Connector line */}
                                {i < trialSteps.length - 1 && (
                                    <div className="hidden md:block absolute top-8 left-[60%] w-full h-0.5 bg-gradient-to-r from-white/40 to-transparent" />
                                )}

                                <div className="relative p-6 rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 hover:border-white/30 transition-all group">
                                    <div className="w-16 h-16 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                        <item.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <div className="text-center mb-3">
                                        <span className="text-xs text-white font-bold uppercase tracking-wide">{item.time}</span>
                                    </div>
                                    <h3 className="text-white font-bold mb-2 text-center">{item.title}</h3>
                                    <p className="text-sm text-gray-400 leading-relaxed text-center">
                                        {item.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Trial form */}
                    <div className="max-w-2xl mx-auto">
                        <div className="p-10 rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border-2 border-white/30 shadow-2xl shadow-white/10">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-white mb-3">Request trial</h2>
                                <p className="text-gray-300">
                                    See your real GPU metrics. No commitments.
                                </p>
                            </div>

                            <form className="space-y-5" onSubmit={handleSubmit} noValidate>
                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-2">Full name *</label>
                                        <input
                                            type="text"
                                            id="fullName"
                                            name="fullName"
                                            placeholder="John Doe"
                                            className="w-full px-5 py-4 bg-black/60 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                                            required
                                            value={formData.fullName}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="workEmail" className="block text-sm font-medium text-gray-300 mb-2">Work email *</label>
                                        <input
                                            type="email"
                                            id="workEmail"
                                            name="workEmail"
                                            placeholder="you@company.com"
                                            className="w-full px-5 py-4 bg-black/60 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                                            required
                                            value={formData.workEmail}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">Company *</label>
                                        <input
                                            type="text"
                                            id="company"
                                            name="company"
                                            placeholder="Your company name"
                                            className="w-full px-5 py-4 bg-black/60 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                                            required
                                            value={formData.company}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="teamSize" className="block text-sm font-medium text-gray-300 mb-2">Team size *</label>
                                        <select
                                            id="teamSize"
                                            name="teamSize"
                                            className="w-full px-5 py-4 bg-black/60 border border-white/20 rounded-xl text-white focus:border-white focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                                            required
                                            value={formData.teamSize}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select a range</option>
                                            <option value="1-5">1-5 engineers</option>
                                            <option value="6-15">6-15 engineers</option>
                                            <option value="16-40">16-40 engineers</option>
                                            <option value="41+">41+ engineers</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="useCase" className="block text-sm font-medium text-gray-300 mb-2">Primary use case *</label>
                                    <input
                                        type="text"
                                        id="useCase"
                                        name="useCase"
                                        placeholder="LLM inference, RAG, copilots, fine-tuning..."
                                        className="w-full px-5 py-4 bg-black/60 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                                        required
                                        value={formData.useCase}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-300 mb-2">Anything we should know? (optional)</label>
                                    <textarea
                                        rows={3}
                                        id="notes"
                                        name="notes"
                                        placeholder="Traffic patterns, GPU types, compliance requirements..."
                                        className="w-full px-5 py-4 bg-black/60 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/20 transition-all resize-none"
                                        value={formData.notes}
                                        onChange={handleChange}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-white text-black rounded-xl font-bold text-lg hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl shadow-white/30 hover:shadow-white/50 hover:scale-[1.02] group"
                                    disabled={status === 'submitting' || status === 'success'}
                                >
                                    {status === 'success' ? <CheckCircle className="w-6 h-6" /> : <Zap className="w-6 h-6 group-hover:rotate-12 transition-transform" />}
                                    {status === 'success' ? 'Request received ✓' : status === 'submitting' ? 'Sending...' : 'Request trial access'}
                                    {status !== 'success' && <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />}
                                </button>

                                <div className="text-center text-sm" aria-live="polite">
                                    {status === 'success' && (
                                        <p className="text-white font-semibold text-base">
                                            ✓ Check your email. Helm credentials arriving within 1 business day.
                                        </p>
                                    )}
                                    {status === 'error' && (
                                        <p className="text-red-400">
                                            {errorMessage}
                                        </p>
                                    )}
                                </div>
                            </form>

                            {/* Trust signals */}
                            <div className="mt-8 grid md:grid-cols-3 gap-4">
                                {[
                                    { icon: Zap, text: '5 min setup' },
                                    { icon: Shield, text: '100% in your cluster' },
                                    { icon: CheckCircle, text: 'Uninstall anytime' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-2 justify-center text-sm text-gray-300">
                                        <item.icon className="w-4 h-4 text-white" />
                                        <span className="font-medium">{item.text}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-6 p-5 rounded-xl bg-white/5 border border-white/20">
                                <div className="flex items-start gap-3">
                                    <Clock className="w-6 h-6 text-white flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-white font-semibold mb-1">Need help?</p>
                                        <p className="text-sm text-gray-300">
                                            Email <span className="text-white font-semibold">forecaster.k8s@gmail.com</span> for setup support
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* What's included */}
                    <div className="mt-16">
                        <h3 className="text-2xl font-bold text-white mb-8 text-center">Your trial includes</h3>
                        <div className="grid md:grid-cols-3 gap-5">
                            {[
                                { title: 'Full access', detail: 'All features unlocked' },
                                { title: 'Email support', detail: 'Setup help during trial' },
                                { title: 'Clean uninstall', detail: 'One Helm command' },
                            ].map((item, i) => (
                                <div key={i} className="p-6 rounded-xl bg-white/[0.03] border border-white/10 hover:border-white/30 transition-all">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                                        <div>
                                            <div className="text-white font-bold mb-2">{item.title}</div>
                                            <div className="text-sm text-gray-400">{item.detail}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 border-t border-white/10">
                <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <ForecasterLogo />
                    <p className="text-gray-500 text-sm">© 2026 Forecaster</p>
                </div>
            </footer>
        </div>
    );
};
